const LEADERSHIP_PT = { coach: "Secretário", administrator: "Presidente", both: "Secretário/Presidente" };
import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Lock, Unlock, Save, CheckCircle, X, Clock, Trash2, Download, Mail, LayoutGrid, List, Send, QrCode, Wand2, ArrowRight, Crown, Users, Trophy } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ApplyQrPanel } from "@/components/ApplyQrPanel";

const CLUBS = ["Analistas", "Diplomatas", "Sentinelas", "Exploradores"];
const CLUB_EMOJI = { Analistas: "🧠", Diplomatas: "🤝", Sentinelas: "🛡️", Exploradores: "🚀" };
const STATUS = [
  { k: "pending", label: "Pendente", color: "#EAB308" },
  { k: "accepted", label: "Aceite", color: "#22C55E" },
  { k: "rejected", label: "Não selecionado", color: "#737373" },
];

export default function ApplicationsAdmin() {
  const [stats, setStats] = useState(null);
  const [apps, setApps] = useState([]);
  const [filterClub, setFilterClub] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [settings, setSettings] = useState(null);
  const [detail, setDetail] = useState(null);
  const [view, setView] = useState("clubs");
  const [sendingReminder, setSendingReminder] = useState(false);
  const [promotion, setPromotion] = useState(null);
  const [promoting, setPromoting] = useState(false);
  const navigate = useNavigate();
  const [draft, setDraft] = useState(null);
  const [suggestion, setSuggestion] = useState(null);

  const refresh = async () => {
    try {
      const [sR, aR, stR] = await Promise.all([
        api.get("/applications/stats"),
        api.get("/applications"),
        api.get("/applications/settings"),
      ]);
      setStats(sR.data);
      setApps(aR.data);
      setSettings(stR.data);
      setDraft({
        deadline_iso: stR.data.deadline_iso || "",
        quota_active: stR.data.quota_active ?? 8,
        allowed_domains: stR.data.allowed_domains || "kwportugal.pt,kwlead.pt",
      });
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { refresh(); }, []);

  const filtered = useMemo(() => {
    return apps.filter((a) => {
      if (filterClub && a.club !== filterClub) return false;
      if (filterStatus && (a.status || "pending") !== filterStatus) return false;
      return true;
    });
  }, [apps, filterClub, filterStatus]);

  const saveSettings = async (patch) => {
    try {
      await api.put("/applications/settings", patch);
      toast.success("Configuração guardada");
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  /* Commits a draft field only if it actually changed and is valid.
     Native date inputs fire onChange mid-typing ("0026-10-11"), so we save on blur. */
  const commitDraft = (key) => {
    const val = draft[key];
    if (key === "deadline_iso") {
      if (val && !/^\d{4}-\d{2}-\d{2}$/.test(val)) return;
      if (val && (Number(val.slice(0, 4)) < 2000 || Number(val.slice(0, 4)) > 2100)) {
        toast.error("Ano inválido. Usa uma data entre 2000 e 2100.");
        setDraft({ ...draft, deadline_iso: settings.deadline_iso || "" });
        return;
      }
    }
    if (key === "quota_active") {
      const n = parseInt(val, 10);
      if (Number.isNaN(n)) {
        setDraft({ ...draft, [key]: settings[key] });
        return;
      }
    }
    if (String(val ?? "") === String(settings[key] ?? "")) return;
    saveSettings({ [key]: key.startsWith("quota") ? parseInt(val, 10) : val });
  };

  const setAppStatus = async (id, status) => {
    try {
      const r = await api.patch(`/applications/${id}`, { status });
      toast.success(r.data?.email_sent ? "Estado atualizado · email enviado ao candidato" : "Estado atualizado");
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const removeApp = async (app) => {
    if (!window.confirm(`Apagar definitivamente a candidatura de ${app.user_name}?\n\nO consultor mantém-se na base — só a candidatura é apagada e a pessoa pode voltar a candidatar-se.`)) return;
    try {
      await api.delete(`/applications/${app.id}`);
      toast.success("Candidatura apagada");
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const bulkDelete = async () => {
    const typed = window.prompt(
      `Apagar TODAS as ${stats.total} candidaturas?\n\nOs consultores mantêm-se na base — só as candidaturas são apagadas.\nEscreve APAGAR para confirmar:`);
    if (typed !== "APAGAR") return;
    try {
      const r = await api.post("/applications/bulk-delete", { confirm: "APAGAR" });
      toast.success(`${r.data.deleted} candidatura(s) apagada(s)`);
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const exportFile = async (format) => {
    try {
      const r = await api.get(`/applications/export?format=${format}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `candidaturas.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openSuggestion = async () => {
    try {
      const r = await api.post("/applications/suggest-selection?commit=false");
      if (!r.data.changes) return toast.info("A seleção atual já segue a ordem de resposta ao MBTI");
      setSuggestion(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const commitSuggestion = async (notify) => {
    try {
      const r = await api.post(`/applications/suggest-selection?commit=true&notify=${notify}`);
      toast.success(`${r.data.applied} decisão(ões) aplicada(s)${r.data.emails_sent ? ` · ${r.data.emails_sent} email(s) enviado(s)` : ""}`);
      setSuggestion(null);
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openPromotion = async () => {
    try {
      const r = await api.post("/applications/promote-accepted", { dry_run: true });
      if (!r.data.promoted && !r.data.blocked?.length && !r.data.already_competing) {
        return toast.info("Não há candidaturas aceites para promover");
      }
      setPromotion(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const confirmPromotion = async () => {
    setPromoting(true);
    try {
      const r = await api.post("/applications/promote-accepted", { from_week: promotion.week });
      const d = r.data || {};
      toast.success(`${d.promoted} consultor(es) no plantel a coopetir desde a semana ${d.week}`);
      if (d.unmatched?.length) toast.warning(`Sem ficha de consultor: ${d.unmatched.join(", ")}`);
      setPromotion(null);
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
    setPromoting(false);
  };

  const runDeadlineReminder = async (force) => {    setSendingReminder(true);
    try {
      const r = await api.post(`/applications/deadline-reminder/run?force=${force ? "true" : "false"}`);
      const d = r.data || {};
      if (d.sent > 0) toast.success(`${d.sent} lembrete(s) enviado(s)`);
      else toast.info(`Nenhum email enviado (${d.reason || "sem destinatários pendentes"})`);
    } catch (err) { toast.error(formatApiError(err)); }
    setSendingReminder(false);
  };

  if (!stats || !settings || !draft) return <div className="kw-overline">A carregar…</div>;

  return (
    <div className="space-y-6" data-testid="applications-admin">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="kw-overline">Admin</div>
          <h1 className="font-display text-5xl md:text-6xl font-black uppercase tracking-tighter">
            Candidaturas
          </h1>
          <p className="text-sm text-[#A3A3A3] mt-2">
            {stats.total} candidatura(s) · {stats.quota_active} concorrentes por clube.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={openPromotion} disabled={!stats.total}
            className="px-3 py-2 border border-[#22C55E] bg-[#22C55E]/10 text-[10px] uppercase tracking-widest font-bold text-[#22C55E] hover:bg-[#22C55E]/20 inline-flex items-center gap-1.5 transition-colors disabled:opacity-40"
            data-testid="apps-promote-accepted">
            <Trophy className="w-3.5 h-3.5" /> Promover aceites ao plantel
          </button>
          <button onClick={openSuggestion} disabled={!stats.total}
            className="px-3 py-2 border border-[#A855F7] text-[10px] uppercase tracking-widest font-bold text-[#A855F7] hover:bg-[#A855F7]/10 inline-flex items-center gap-1.5 transition-colors disabled:opacity-40" data-testid="apps-suggest">
            <Wand2 className="w-3.5 h-3.5" /> Sugerir seleção
          </button>
          <button onClick={() => exportFile("xlsx")} className="px-3 py-2 border border-[#333] text-[10px] uppercase tracking-widest font-bold text-[#A3A3A3] hover:text-white hover:border-white inline-flex items-center gap-1.5 transition-colors" data-testid="apps-export-xlsx">
            <Download className="w-3.5 h-3.5" /> Excel
          </button>
          <button onClick={() => exportFile("csv")} className="px-3 py-2 border border-[#333] text-[10px] uppercase tracking-widest font-bold text-[#A3A3A3] hover:text-white hover:border-white inline-flex items-center gap-1.5 transition-colors" data-testid="apps-export-csv">
            <Download className="w-3.5 h-3.5" /> CSV
          </button>
          <button onClick={() => runDeadlineReminder(true)} disabled={sendingReminder}
            className="px-3 py-2 border border-[#EAB308] text-[10px] uppercase tracking-widest font-bold text-[#EAB308] hover:bg-[#EAB308]/10 inline-flex items-center gap-1.5 transition-colors disabled:opacity-50" data-testid="apps-send-reminder">
            <Mail className="w-3.5 h-3.5" /> {sendingReminder ? "A enviar…" : "Lembrar em falta"}
          </button>
          <button onClick={bulkDelete} disabled={!stats.total}
            className="px-3 py-2 border border-[#E50000] text-[10px] uppercase tracking-widest font-bold text-[#E50000] hover:bg-[#E50000]/10 inline-flex items-center gap-1.5 transition-colors disabled:opacity-40"
            data-testid="apps-bulk-delete">
            <Trash2 className="w-3.5 h-3.5" /> Apagar todas
          </button>
        </div>
      </div>

      {/* Settings */}
      <ApplyQrPanel isOpen={settings.is_open} deadlineIso={settings.deadline_iso} />

      <div className="kw-card p-5 grid md:grid-cols-5 gap-4 items-start" data-testid="apps-settings">
        <div className="md:col-span-1">
          <label className="kw-overline block mb-1 text-[10px]">Período</label>
          <button onClick={() => saveSettings({ is_open: !settings.is_open })}
            className={`w-full px-4 py-2 text-xs uppercase tracking-widest font-bold border inline-flex items-center justify-center gap-2 ${settings.is_open ? "bg-[#22C55E]/10 border-[#22C55E] text-[#22C55E]" : "bg-[#737373]/10 border-[#737373] text-[#A3A3A3]"}`}
            data-testid="apps-toggle-open">
            {settings.is_open ? <><Unlock className="w-3 h-3" /> Aberto</> : <><Lock className="w-3 h-3" /> Fechado</>}
          </button>
        </div>
        <div>
          <label className="kw-overline block mb-1 text-[10px]">Data limite (opcional)</label>
          <div className="flex gap-1">
            <input type="date" className="kw-input !py-2" min="2026-01-01" max="2100-12-31"
              value={draft.deadline_iso}
              onChange={(e) => setDraft({ ...draft, deadline_iso: e.target.value })}
              onBlur={() => commitDraft("deadline_iso")}
              onKeyDown={(e) => { if (e.key === "Enter") commitDraft("deadline_iso"); }}
              data-testid="apps-deadline" />
            {draft.deadline_iso && (
              <button onClick={() => { setDraft({ ...draft, deadline_iso: "" }); saveSettings({ deadline_iso: "" }); }}
                className="px-2 border border-[#333] text-[#737373] hover:text-white hover:border-white transition-colors"
                title="Remover data limite" data-testid="apps-deadline-clear">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <div className="text-[9px] text-[#737373] mt-1">
            {draft.deadline_iso !== (settings.deadline_iso || "")
              ? "Sai do campo (ou Enter) para guardar"
              : settings.deadline_iso
                ? `Fecha ${new Date(settings.deadline_iso + "T12:00:00").toLocaleDateString("pt-PT", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })} · lembrete 48h antes`
                : "Sem data limite · fecha quando desligares o período"}
          </div>
        </div>
        <div>
          <label className="kw-overline block mb-1 text-[10px]">Concorrentes / clube</label>
          <input type="number" min="1" className="kw-input !py-2"
            value={draft.quota_active}
            onChange={(e) => setDraft({ ...draft, quota_active: e.target.value })}
            onBlur={() => commitDraft("quota_active")}
            onKeyDown={(e) => { if (e.key === "Enter") commitDraft("quota_active"); }}
            data-testid="apps-quota-active" />
        </div>
        <div>
          <label className="kw-overline block mb-1 text-[10px]">Domínios de email aceites</label>
          <input type="text" className="kw-input !py-2"
            value={draft.allowed_domains}
            onChange={(e) => setDraft({ ...draft, allowed_domains: e.target.value })}
            onBlur={() => commitDraft("allowed_domains")}
            placeholder="kwportugal.pt,kwlead.pt"
            data-testid="apps-domains" />
          <div className="text-[9px] text-[#737373] mt-1">Validação do formulário público (separar por vírgula)</div>
        </div>
      </div>

      <LeadershipCandidates />

      {/* Stats per club */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {CLUBS.map((c) => {
          const row = stats.by_club[c] || { pending: 0, accepted: 0, rejected: 0, total: 0 };
          return (
            <div key={c} className="kw-card p-4" data-testid={`stats-${c}`}>
              <div className="text-2xl mb-1">{CLUB_EMOJI[c]}</div>
              <div className="font-display font-black uppercase text-sm">{c}</div>
              <div className="text-xs text-[#A3A3A3] mt-1">{row.total} candidaturas</div>
              <div className="mt-2 space-y-0.5 text-[10px]">
                <div className="text-[#22C55E]">✓ {row.accepted}/{stats.quota_active} aceites</div>
                <div className="text-[#EAB308]">⌛ {row.pending} pendentes</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap items-center">
        <div className="flex border border-[#333] mr-2">
          <button onClick={() => setView("clubs")} className={`px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold inline-flex items-center gap-1.5 ${view === "clubs" ? "bg-[#E50000] text-white" : "text-[#A3A3A3] hover:text-white"}`} data-testid="apps-view-clubs">
            <LayoutGrid className="w-3 h-3" /> Clubes
          </button>
          <button onClick={() => setView("list")} className={`px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold inline-flex items-center gap-1.5 ${view === "list" ? "bg-[#E50000] text-white" : "text-[#A3A3A3] hover:text-white"}`} data-testid="apps-view-list">
            <List className="w-3 h-3" /> Lista
          </button>
        </div>
        <span className="kw-overline text-[10px]">Clube:</span>
        <button onClick={() => setFilterClub("")} className={`px-3 py-1 text-[10px] uppercase tracking-widest font-bold border ${!filterClub ? "bg-[#E50000] border-[#E50000] text-white" : "border-[#333] text-[#A3A3A3]"}`}>Todos</button>
        {CLUBS.map((c) => (
          <button key={c} onClick={() => setFilterClub(c)} className={`px-3 py-1 text-[10px] uppercase tracking-widest font-bold border ${filterClub === c ? "bg-[#E50000] border-[#E50000] text-white" : "border-[#333] text-[#A3A3A3]"}`}>{CLUB_EMOJI[c]} {c}</button>
        ))}
        <span className="kw-overline text-[10px] ml-4">Estado:</span>
        <button onClick={() => setFilterStatus("")} className={`px-3 py-1 text-[10px] uppercase tracking-widest font-bold border ${!filterStatus ? "bg-[#E50000] border-[#E50000] text-white" : "border-[#333] text-[#A3A3A3]"}`}>Todos</button>
        {STATUS.map((s) => (
          <button key={s.k} onClick={() => setFilterStatus(s.k)} className={`px-3 py-1 text-[10px] uppercase tracking-widest font-bold border ${filterStatus === s.k ? "border-[#E50000] text-white bg-[#E50000]" : ""}`}
            style={filterStatus === s.k ? {} : { borderColor: s.color, color: s.color }}>{s.label}</button>
        ))}
      </div>

      {/* Kanban by club */}
      {view === "clubs" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="apps-kanban">
          {(filterClub ? [filterClub] : CLUBS).map((c) => {
            const items = filtered.filter((a) => a.club === c)
              .sort((x, y) => (x.queue_position || 999) - (y.queue_position || 999));
            const q = stats.by_club[c] || { accepted: 0 };
            const over = q.accepted > stats.quota_active;
            return (
              <div key={c} className="kw-card p-0 flex flex-col" data-testid={`kanban-${c}`}>
                <div className="p-3 border-b border-[#222] sticky top-0 bg-[#0A0A0A]/95 backdrop-blur-sm z-10">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{CLUB_EMOJI[c]}</span>
                    <span className="font-display font-black uppercase text-sm flex-1">{c}</span>
                    <span className="text-[10px] text-[#737373]">{items.length}</span>
                  </div>
                  <div className="mt-2 h-1 bg-[#1A1A1A] overflow-hidden">
                    <div className="h-full" style={{
                      width: `${Math.min(100, (q.accepted / Math.max(1, stats.quota_active)) * 100)}%`,
                      background: over ? "#E50000" : "#22C55E",
                    }} />
                  </div>
                  <div className="flex gap-3 mt-1.5 text-[10px]">
                    <span className={over ? "text-[#E50000] font-bold" : "text-[#22C55E]"}>✓ {q.accepted}/{stats.quota_active}</span>
                  </div>
                </div>
                <div className="p-2 space-y-2 min-h-[120px]">
                  {items.map((a) => {
                    const s = STATUS.find((x) => x.k === (a.status || "pending")) || STATUS[0];
                    return (
                      <div key={a.id} onClick={() => setDetail(a)}
                        className="bg-[#0F0F0F] border-l-2 p-2.5 cursor-pointer hover:bg-[#151515] transition-colors"
                        style={{ borderLeftColor: s.color }} data-testid={`kanban-card-${a.id}`}>
                        <div className="flex items-start gap-2">
                          <span className="font-display font-black text-sm text-[#555] shrink-0 w-6" title="Lugar na fila pela resposta ao MBTI">
                            #{a.queue_position}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="font-bold text-xs truncate">{a.user_name}</div>
                            <div className="text-[10px] text-[#737373] truncate">
                              {a.mbti_answered_at
                                ? `${a.mbti || "—"} · ${new Date(a.mbti_answered_at).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}`
                                : a.mbti ? `${a.mbti} · sem hora (prioritário)` : a.user_email}
                            </div>
                          </div>
                          {a.source === "public" && <QrCode className="w-3 h-3 text-[#A855F7] shrink-0" title="Candidatura via QR" />}
                          {a.decision_email_sent_at && <Send className="w-3 h-3 text-[#22C55E] shrink-0" title="Email enviado" />}
                        </div>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border" style={{ borderColor: s.color, color: s.color }}>{s.label}</span>
                          <div className="flex gap-0.5" onClick={(e) => e.stopPropagation()}>
                            <button onClick={() => setAppStatus(a.id, "accepted")} className="text-[#22C55E] hover:bg-[#22C55E]/10 p-1" title="Aceitar"><CheckCircle className="w-3.5 h-3.5" /></button>
                            <button onClick={() => setAppStatus(a.id, "rejected")} className="text-[#737373] hover:bg-white/10 p-1" title="Não selecionado"><X className="w-3.5 h-3.5" /></button>
                            <button onClick={() => removeApp(a)} className="text-[#E50000] hover:bg-[#E50000]/10 p-1"
                              title="Apagar candidatura" data-testid={`kanban-delete-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {items.length === 0 && <div className="p-4 text-center text-[10px] uppercase tracking-widest text-[#444]">Vazio</div>}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
      /* List */
      <div className="kw-card overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="kw-overline border-b border-[#222]">
            <tr>
              <th className="p-3">Candidato</th>
              <th className="p-3">Clube</th>
              <th className="p-3">Compromisso</th>
              <th className="p-3" title="Se não for selecionado, aceita um cargo">Cargo?</th>
              <th className="p-3">Estado</th>
              <th className="p-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((a) => {
              const s = STATUS.find((x) => x.k === (a.status || "pending")) || STATUS[0];
              return (
                <tr key={a.id} className="border-b border-[#161616] hover:bg-[#111] cursor-pointer" onClick={() => setDetail(a)} data-testid={`app-row-${a.id}`}>
                  <td className="p-3">
                    <div className="font-bold flex items-center gap-1.5">
                      {a.user_name}
                      {a.source === "public" && <QrCode className="w-3 h-3 text-[#A855F7]" title="Candidatura via QR" />}
                    </div>
                    <div className="text-[10px] text-[#737373]">{a.user_email}</div>
                  </td>
                  <td className="p-3 text-xs">{CLUB_EMOJI[a.club]} {a.club}</td>
                  <td className="p-3 text-xs">
                    {a.commitment_level === "full" ? <span className="text-[#22C55E]">Total</span> : <span className="text-[#EAB308]">C/ limitações</span>}
                  </td>
                  <td className="p-3 text-xs text-[#EAB308]">{LEADERSHIP_PT[a.leadership_interest] || "—"}</td>
                  <td className="p-3">
                    <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border" style={{ borderColor: s.color, color: s.color }}>{s.label}</span>
                  </td>
                  <td className="p-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => setAppStatus(a.id, "accepted")} className="text-[#22C55E] hover:text-white px-2" title="Aceitar"><CheckCircle className="w-4 h-4 inline" /></button>
                    <button onClick={() => setAppStatus(a.id, "rejected")} className="text-[#737373] hover:text-white px-2" title="Rejeitar"><X className="w-4 h-4 inline" /></button>
                    <button onClick={() => removeApp(a)} className="text-[#E50000] hover:text-white px-2" title="Apagar candidatura"
                      data-testid={`list-delete-${a.id}`}><Trash2 className="w-4 h-4 inline" /></button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan="6" className="p-6 text-center text-[#737373]">Sem candidaturas que correspondam aos filtros.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      )}

      {detail && <ApplicationDetail app={detail} onClose={() => setDetail(null)} onDone={refresh} />}
      {suggestion && (
        <SuggestionReview data={suggestion} onClose={() => setSuggestion(null)} onCommit={commitSuggestion} />
      )}
      {promotion && (
        <PromotionReview data={promotion} busy={promoting} onClose={() => setPromotion(null)}
          onConfirm={confirmPromotion} onRoster={() => navigate("/roster")} />
      )}

    </div>
  );

function PromotionReview({ data, busy, onClose, onConfirm, onRoster }) {
  const clubs = Object.entries(data.by_club || {});
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="kw-card max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}
        data-testid="promotion-review">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <div className="kw-overline text-[#22C55E]">Candidatos → Plantel</div>
            <h2 className="font-display text-3xl font-black uppercase tracking-tighter mt-1">
              {data.promoted ? `Promover ${data.promoted} ao plantel` : "Nada novo para promover"}
            </h2>
            <p className="text-sm text-[#A3A3A3] mt-2">
              Passam a <b className="text-[#22C55E]">coopetir</b> a partir da <b className="text-white">semana {data.week}</b> e
              começam a contar para os rankings e para o clube.
              {data.already_competing ? ` ${data.already_competing} já estavam no plantel e ficam como estão.` : ""}
            </p>
          </div>
          <button onClick={onClose} className="text-[#737373] hover:text-white" data-testid="promotion-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          {clubs.map(([club, names]) => (
            <div key={club} className="border border-[#262626] p-4" data-testid={`promotion-club-${club}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase tracking-widest font-bold">{CLUB_EMOJI[club]} {club}</span>
                <span className="font-display text-xl font-black text-[#22C55E]">{names.length}</span>
              </div>
              <ul className="space-y-1">
                {names.map((n) => (
                  <li key={n} className="text-sm text-[#A3A3A3] flex items-center gap-2">
                    <Users className="w-3 h-3 text-[#555]" /> {n}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="border border-[#262626] p-4 mt-4 text-xs text-[#A3A3A3] flex flex-wrap gap-x-6 gap-y-1"
          data-testid="promotion-summary">
          <span><b className="text-white">{data.accepted_total}</b> aceites no total</span>
          <span><b className="text-[#22C55E]">{data.promoted}</b> a promover</span>
          <span><b className="text-white">{data.already_competing}</b> já no plantel</span>
          {Object.entries(data.accepted_by_club || {}).map(([c, n]) => (
            <span key={c}>{c}: <b className="text-white">{n}</b></span>
          ))}
        </div>

        {data.blocked?.length > 0 && (
          <div className="border border-[#EAB308] bg-[#EAB308]/5 p-4 mt-4" data-testid="promotion-blocked">
            <div className="text-xs uppercase tracking-widest font-bold text-[#EAB308] mb-2">
              {data.blocked.length} a precisar de atenção
            </div>
            <ul className="space-y-1.5">
              {data.blocked.map((b, i) => (
                <li key={i} className="text-sm">
                  <b className="text-white">{b.name}</b>
                  <span className="text-[#737373]"> · {b.club}</span>
                  <div className="text-[#A3A3A3] text-xs">{b.reason}</div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex flex-wrap gap-3 mt-6">
          <button onClick={onConfirm} disabled={busy || !data.promoted} className="kw-btn-primary inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="promotion-confirm">
            <Trophy className="w-4 h-4" /> {busy ? "A promover…" : `Promover ${data.promoted} ao plantel`}
          </button>
          <button onClick={onRoster} className="kw-btn-secondary inline-flex items-center gap-2" data-testid="promotion-goto-roster">
            Ver Plantel da Liga <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

}

/* Quem se disponibilizou para um cargo, clube a clube — base para a escolha dos plantéis. */
function LeadershipCandidates() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    api.get("/applications/leadership-candidates").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;

  return (
    <div className="kw-card p-0" data-testid="leadership-candidates">
      <button onClick={() => setOpen(!open)} className="w-full p-4 flex items-center gap-3 hover:bg-[#111] transition-colors">
        <Crown className="w-4 h-4 text-[#EAB308] shrink-0" />
        <span className="flex-1 text-left">
          <span className="block kw-overline text-[#EAB308]">Para a reunião dos plantéis</span>
          <span className="block font-display text-lg font-black uppercase tracking-tight">
            Candidatos a Cargo · {data.total}
          </span>
        </span>
        <span className="text-[10px] uppercase tracking-widest text-[#737373]">{open ? "fechar" : "abrir"}</span>
      </button>

      {open && (
        <div className="p-4 pt-0">
          <p className="text-xs text-[#A3A3A3] mb-4 max-w-3xl">
            Quem disse que, não sendo selecionado para coopetir, aceita assumir um cargo. O Presidente do Clube é do
            perfil do próprio clube; o Secretário acompanha um clube de perfil diferente do seu, por isso procura-o
            nos outros clubes. A escolha é do plantel; depois registas em Plantel da Liga.
          </p>
          {data.total === 0 ? (
            <div className="text-sm text-[#737373] py-6 text-center" data-testid="leadership-candidates-empty">
              Ainda ninguém se disponibilizou. A pergunta está no formulário de candidatura.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
              {data.clubs.map((row) => (
                <div key={row.club} className="border border-[#222]" data-testid={`leadership-club-${row.club}`}>
                  <div className="p-3 border-b border-[#222] flex items-center gap-2">
                    <span>{CLUB_EMOJI[row.club]}</span>
                    <span className="font-display font-black uppercase text-sm flex-1">{row.club}</span>
                    <span className="text-[10px] text-[#737373]">{row.candidates.length}</span>
                  </div>
                  <div className="px-3 py-1.5 text-[9px] uppercase tracking-widest text-[#737373] border-b border-[#161616]">
                    <span className="text-[#EAB308]">{row.coach} p/ secretário</span> ·{" "}
                    <span className="text-[#A855F7]">{row.administrator} p/ presidente</span>
                  </div>
                  {row.candidates.length === 0 ? (
                    <div className="p-3 text-[11px] text-[#555] italic">ninguém se disponibilizou</div>
                  ) : row.candidates.map((p) => (
                    <div key={p.application_id} className="p-3 border-b border-[#161616] last:border-0"
                      data-testid={`leadership-candidate-${p.application_id}`}>
                      <div className="text-sm font-bold">{p.name}</div>
                      <div className="text-[10px] uppercase tracking-widest text-[#EAB308] mt-0.5">
                        {p.interest_label}
                      </div>
                      <div className="text-[10px] text-[#737373] mt-0.5 font-mono">
                        {p.mbti || "sem MBTI"}
                        {p.current_role && <span className="text-[#22C55E]"> · já é {p.current_role === "coach" ? "secretário" : p.current_role === "administrator" ? "presidente do clube" : "presidente da liga"}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
          {data.no_club?.length > 0 && (
            <div className="mt-3 text-[11px] text-[#737373]" data-testid="leadership-candidates-noclub">
              Sem clube indicado: {data.no_club.map((p) => p.name).join(", ")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SuggestionReview({ data, onClose, onCommit }) {  const [notify, setNotify] = useState(false);
  const label = { accepted: "Aceite", pending: "Pendente", rejected: "Não selecionado" };
  const color = { accepted: "#22C55E", pending: "#EAB308", rejected: "#737373" };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="kw-card max-w-5xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}
        data-testid="suggestion-review">
        <div className="flex items-start justify-between gap-4 mb-1">
          <div>
            <div className="kw-overline text-[10px]">Proposta · ordem de resposta ao MBTI</div>
            <h2 className="font-display text-3xl font-black uppercase tracking-tight">Sugerir seleção</h2>
          </div>
          <button onClick={onClose} className="text-[#737373] hover:text-white" data-testid="suggestion-close"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-xs text-[#A3A3A3] mb-5 max-w-2xl leading-relaxed">
          Os primeiros <b className="text-white">{data.quota_active}</b> de cada clube ficam <b className="text-[#22C55E]">aceites</b>,
          pela ordem em que responderam ao MBTI. Quem fez o teste <b className="text-white">sem registo de hora</b> fica à frente.
          Os restantes ficam pendentes.
          <b className="text-[#EAB308]"> Nada é gravado até confirmares.</b>
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {CLUBS.map((c) => {
            const rows = data.plan[c] || [];
            if (!rows.length) return null;
            return (
              <div key={c} className="border border-[#222]" data-testid={`suggestion-${c}`}>
                <div className="px-3 py-2 border-b border-[#222] flex items-center gap-2 bg-[#0F0F0F]">
                  <span>{CLUB_EMOJI[c]}</span>
                  <span className="font-display font-black uppercase text-xs flex-1">{c}</span>
                  <span className="text-[10px] text-[#737373]">{rows.filter((r) => r.changed).length} alteração(ões)</span>
                </div>
                <div className="divide-y divide-[#1A1A1A]">
                  {rows.map((r) => (
                    <div key={r.id} className={`px-3 py-2 flex items-center gap-2 text-xs ${r.changed ? "" : "opacity-45"}`}>
                      <span className="font-display font-black text-[#555] w-7 shrink-0">#{r.queue_position}</span>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold truncate">{r.user_name}</div>
                        <div className="text-[9px] text-[#737373]">
                          {r.mbti_answered_at
                            ? new Date(r.mbti_answered_at).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
                            : "sem hora · prioritário"}
                        </div>
                      </div>
                      {r.changed ? (
                        <div className="flex items-center gap-1.5 shrink-0">
                          <span className="text-[9px] uppercase tracking-widest" style={{ color: color[r.current_status] }}>{label[r.current_status]}</span>
                          <ArrowRight className="w-3 h-3 text-[#555]" />
                          <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border"
                            style={{ borderColor: color[r.proposed_status], color: color[r.proposed_status] }}>
                            {label[r.proposed_status]}
                          </span>
                        </div>
                      ) : (
                        <span className="text-[9px] uppercase tracking-widest text-[#555] shrink-0">sem alteração</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 mt-6 pt-4 border-t border-[#222]">
          <label className="flex items-center gap-2 text-xs text-[#A3A3A3] cursor-pointer">
            <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)}
              className="accent-[#E50000]" data-testid="suggestion-notify" />
            Enviar email de decisão aos candidatos afetados
          </label>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 border border-[#333] text-[10px] uppercase tracking-widest font-bold text-[#A3A3A3] hover:text-white">
              Cancelar
            </button>
            <button onClick={() => onCommit(notify)} className="kw-btn-primary !py-2 !px-5 text-[10px]" data-testid="suggestion-confirm">
              Aplicar {data.changes} alteração(ões)
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ApplicationDetail({ app, onClose, onDone }) {
  const [notes, setNotes] = useState(app.admin_notes || "");
  const [status, setStatus] = useState(app.status || "pending");
  const [notify, setNotify] = useState(true);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/applications/${app.id}`, { status, admin_notes: notes, notify });
      toast.success(r.data?.email_sent ? "Guardado · email enviado ao candidato" : "Guardado");
      onDone(); onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };
  const resend = async () => {
    try {
      await api.post(`/applications/${app.id}/resend-decision`);
      toast.success("Email reenviado");
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async () => {
    if (!window.confirm("Apagar esta candidatura?")) return;
    try {
      await api.delete(`/applications/${app.id}`);
      toast.success("Apagada"); onDone(); onClose();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0A0A0A] border border-[#333] max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()} data-testid="app-detail">
        <div className="flex items-center justify-between p-4 border-b border-[#222]">
          <div className="font-display font-black uppercase text-lg">{app.user_name}</div>
          <button onClick={onClose} className="text-[#737373] hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-4">
          <div><div className="kw-overline text-[10px] mb-1">Email</div><div className="text-sm">{app.user_email}</div></div>
          <div><div className="kw-overline text-[10px] mb-1">Clube</div><div className="text-sm">{CLUB_EMOJI[app.club]} {app.club}</div></div>
          <div><div className="kw-overline text-[10px] mb-1">Origem</div><div className="text-sm">{app.source === "public" ? "QR / Link público" : "Plataforma (com login)"}</div></div>
          <div>
            <div className="kw-overline text-[10px] mb-1">MBTI · fila</div>
            <div className="text-sm">
              {app.mbti || "—"} · <span className="text-[#A855F7] font-bold">#{app.queue_position}</span>
              <div className="text-[10px] text-[#737373]">
                {app.mbti_answered_at
                  ? `Respondeu ${new Date(app.mbti_answered_at).toLocaleString("pt-PT")}${app.mbti_source ? ` · ${app.mbti_source}` : ""}`
                  : "Sem registo de hora — fica à frente na fila"}
              </div>
            </div>
          </div>
          {app.kw_uid && <div><div className="kw-overline text-[10px] mb-1">KW UID</div><div className="text-sm font-mono">{app.kw_uid}</div></div>}
          <div><div className="kw-overline text-[10px] mb-1">Motivação</div><div className="text-sm whitespace-pre-wrap bg-[#0F0F0F] border border-[#222] p-3">{app.motivation}</div></div>
          <div><div className="kw-overline text-[10px] mb-1">O que quer descobrir / transformar</div><div className="text-sm whitespace-pre-wrap bg-[#0F0F0F] border border-[#222] p-3">{app.discovery_goal}</div></div>
          <div className="grid grid-cols-2 gap-3">
            <div><div className="kw-overline text-[10px] mb-1">Compromisso</div><div className="text-sm">{app.commitment_level === "full" ? "Total" : "Com limitações"}</div></div>
            <div><div className="kw-overline text-[10px] mb-1">Se não for selecionado</div><div className="text-sm">
              {app.leadership_interest && app.leadership_interest !== "none"
                ? (app.leadership_interest === "coach" ? "Aceita ser Secretário do Clube"
                  : app.leadership_interest === "administrator" ? "Aceita ser Presidente do Clube"
                  : "Aceita Secretário ou Presidente")
                : "—"}
            </div></div>
          </div>
          {app.commitment_notes && (
            <div><div className="kw-overline text-[10px] mb-1">Limitações descritas</div><div className="text-sm bg-[#0F0F0F] border border-[#222] p-3">{app.commitment_notes}</div></div>
          )}
          <div className="grid grid-cols-2 gap-3 pt-3 border-t border-[#222]">
            <div>
              <label className="kw-overline text-[10px] mb-1 block">Decisão</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="kw-input !py-2" data-testid="app-detail-status">
                {STATUS.map((s) => <option key={s.k} value={s.k}>{s.label}</option>)}
              </select>
            </div>
            <div className="flex flex-col justify-end gap-1">
              <label className="flex items-center gap-2 text-xs text-[#A3A3A3] cursor-pointer">
                <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} className="accent-[#E50000]" data-testid="app-detail-notify" />
                Avisar o candidato por email
              </label>
              {app.decision_email_sent_at && (
                <button onClick={resend} className="text-[10px] uppercase tracking-widest font-bold text-[#3B82F6] hover:text-white inline-flex items-center gap-1 self-start" data-testid="app-detail-resend">
                  <Send className="w-3 h-3" /> Reenviar email ({app.decision_email_sent_at.slice(0, 10)})
                </button>
              )}
            </div>
          </div>
          <div>
            <label className="kw-overline text-[10px] mb-1 block">Nota para o candidato (opcional)</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="kw-input !py-2 min-h-[80px]" placeholder="Mensagem visível ao candidato…" data-testid="app-detail-notes" />
          </div>
        </div>
        <div className="flex items-center gap-2 p-4 border-t border-[#222]">
          <button onClick={remove} className="text-[#E50000] text-xs uppercase tracking-widest font-bold flex items-center gap-1"><Trash2 className="w-4 h-4" /> Apagar</button>
          <div className="flex-1" />
          <button onClick={onClose} className="text-[#737373] text-xs uppercase tracking-widest font-bold px-3 py-2">Cancelar</button>
          <button onClick={save} disabled={saving} className="kw-btn-primary flex items-center gap-2"><Save className="w-4 h-4" /> Guardar</button>
        </div>
      </div>
    </div>
  );
}
